import {
  BUILTIN_ALTERNATIVE_SHAPES,
  DEFAULT_PREFERRED_ALTERNATIVE_IDS,
  type AlternativeShape,
} from '@/music/alternativeShapes';
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import type { PointerEvent as ReactPointerEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useHoldStrum } from '@/audio/useHoldStrum';
import type { BandArrangement } from '@/audio/useBandArrangement';
import {
  buildScoreView,
  useAnalysisLane,
  useBandPlayheadBeat,
  usePlayableChordSymbols,
} from '@/components/scoreShared';
import { chartToSong } from '@/music/chart';
import { chordSymbol, type ChordTypeId } from '@/music/chords';
import { buildArrangementVoicings } from '@/music/notation/arrangement';
import { voiceLeadPianoMelody } from '@/music/pianoMelody';
import { parseChordLabel } from '@/music/notation/chordLabel';
import type { ChordSymbolStyle } from '@/music/notation/chordLabel';
import type { SequenceChord } from '@/music/song';
import type { StringSet } from '@/music/tuning';
import { topNoteOf, type ChordVoicing, type Inversion } from '@/music/voicing';
import type { OptimizedChord } from '@/music/voiceLeading';
import { tuneArrangementOf, useStore } from '@/state/store';
import { pianoPinnedVoicing, pinKeyResolver, pinLane, type PinPlan } from '@/music/voicingPins';
import { effectivePianoFamily } from '@/music/pianoLeftHand';
import { getChordPlayer } from '@/audio/player';
import { ChordDiagram, FRETBOARD_CENTER_RATIO } from './ChordDiagram';
import { GuitarChartView } from './GuitarChartView';
import { PianoKeyboardDiagram } from './PianoKeyboardDiagram';
import { KEYBOARD_NOMINAL_ASPECT, keyboardAspect } from './pianoKeyboardGeometry';
import { useInspector, type InspectPinContext } from './inspectorContext';
import { ScoreView, type ScoreSystemOverlayContext } from './ScoreView';
import type { DrawnMeasure } from '@/music/notation/drawn';
import { useNotationMetrics } from './useNotationMetrics';
import { layoutDiagramCenters } from './arrangementCollision';
import { scaleFactor, scaleVars } from '@/state/displayScale';
import { usePassageSelection } from '@/components/passageSelection';
import { PagedSheet, SheetPageCards } from './PagedSheet';
import { PrintSheet } from './PrintSheet';
import { usePrintMode } from './printMode';
import { useScorePages } from './useScorePages';

/** Whether the pane shows its grips over notation or as a diagram-only sheet. */
export type ArrangementLayout = 'score' | 'voicings';
/** Which voicing arrangement the pane renders: the melody-led chord-melody
 * plan, or the harmony-led backing ("comping") grips — the same voice-led
 * line the classic Voicings sheet shows, here aligned over the notation. */
export type ArrangementLens = 'melody' | 'comping';

type DiagramVoicing = OptimizedChord<SequenceChord>;
/** A row of the melody lens's diagram sheet: the grip, plus the keyboard
 * stack and the pin flag when the Chords menu draws piano. */
type DiagramRow = DiagramVoicing & {
  midis?: number[];
  hands?: { left: number[]; right: number[] };
  pinnedVoicing?: { midis: number[] };
};

/** The fields the overlay actually draws — satisfied by both the melody-led
 * ArrangementChord voicings and the mapped comping grips. */
interface OverlayVoicing {
  id: string;
  barIndex: number;
  beat: number;
  voicedSymbol: string;
  displayRoot: string;
  chordType: ChordTypeId;
  stringSet: StringSet;
  inversion: Inversion;
  fingering: ChordVoicing;
  targetTopNote?: string | null;
  /** The piano realization, when the Chords menu draws keyboards: the comping
   * lens sources `band.pianoOptimized ?? band.optimized` — the same seam the
   * diagram sheet uses — so the overlay follows the menu for free. */
  midis?: number[];
  hands?: { left: number[]; right: number[] };
  /** The pin picker's inputs (voicingPins.ts): the grip's position key, the
   * chord as the engine saw it, and whether what is drawn is a pin. */
  pinKey?: string;
  pinned?: boolean;
  root: string;
  targetTopMidi?: number | null;
  voicingNotes?: string[];
  bass?: string;
}

/**
 * Grip width in staff-spaces, sized from the DOTS: a marker is r=10 in the
 * diagram's 200-unit viewBox (10% of its width), so at 10 spaces a dot's
 * diameter is exactly one staff space — the size of a notehead. At the old 8
 * the dots read 20% smaller than the music they sit above, which is why the
 * grips felt hard to read at every zoom. Crowded systems still squeeze below
 * the preferred width exactly as before (layoutDiagramCenters).
 */
const DIAGRAM_WIDTH_SPACES = 10;
const DIAGRAM_GAP_SPACES = 0.65;
// The untitled card's viewBox after its fixed 13-unit crop: 200 × 227.
const DIAGRAM_VIEWBOX_ASPECT = 227 / 200;
const DIAGRAM_HEIGHT_SPACES = DIAGRAM_WIDTH_SPACES * DIAGRAM_VIEWBOX_ASPECT;
/** Keyboard grips are WIDE and short where fretboards are tall and narrow:
 * a two-octave keyboard at the fretboard's 10 spaces is ~0.7 spaces per white
 * key — unreadable, the very complaint the compact windows fixed on the
 * sheet. 17 spaces reads; the lane it needs is SHORTER than the fretboard's
 * (keyboard viewBox 100 × 42). */
const KEYBOARD_WIDTH_SPACES = 17;
/** The lane is reserved for the NOMINAL two-octave strip; each voicing then
 * draws its own trimmed window at that key height, narrower where it spans
 * less, and shrinks to the slot only where it would span more. */
const KEYBOARD_VIEWBOX_ASPECT = KEYBOARD_NOMINAL_ASPECT;
const KEYBOARD_HEIGHT_SPACES = KEYBOARD_WIDTH_SPACES * KEYBOARD_VIEWBOX_ASPECT;
/** Band above each diagram holding its chord symbol. */
const LABEL_SPACES = 2.3;
/**
 * Air below the diagram so it never touches the engraved chord lane.
 *
 * `aboveNeeds` alone is not enough: it reserves the chord row's nominal depth,
 * but a symbol's INK box runs higher than that (font ascent above the raised
 * quality), so a diagram sitting flush on the reserved band overlaps the
 * symbol by ~3px. Measured, not guessed — 1.4 spaces turns −3.0px into
 * roughly +11px at every scale.
 */
const CHORD_CLEARANCE_SPACES = 1.4;
/**
 * Breathing room between the PREVIOUS system's lowest ink and this lane's
 * chord symbol. The engraver leaves a fixed 1.5 spaces there on its own —
 * enough for staves, cramped once a grip stack rides in between. The pad is
 * part of the lane (top edge), so only systems that draw grips pay it and
 * it scales with the score like everything else.
 */
const OVERLAY_TOP_PAD_SPACES = 1.0;
const OVERLAY_SPACES =
  OVERLAY_TOP_PAD_SPACES + LABEL_SPACES + DIAGRAM_HEIGHT_SPACES + CHORD_CLEARANCE_SPACES;
const OVERLAY_SPACES_PIANO =
  OVERLAY_TOP_PAD_SPACES + LABEL_SPACES + KEYBOARD_HEIGHT_SPACES + CHORD_CLEARANCE_SPACES;

function ArrangementDiagram({
  chord,
  label,
  x,
  y,
  diagramWidth,
  chordSymbolStyle,
  highlightAvoid,
  plan,
}: {
  chord: OverlayVoicing;
  /** The VOICED symbol — the grip's own spelling, which may recolour the
   *  written chord (the engraved lane keeps the written text). */
  label: string;
  x: number;
  y: number;
  diagramWidth: number;
  chordSymbolStyle: ChordSymbolStyle;
  /** Tint a kept b9-clash grip (the melody-forced override of Avoid b9). */
  highlightAvoid: boolean;
  /** Which plan drew this grip — the lane a pin made here belongs to. */
  plan: PinPlan;
}) {
  const { inspect } = useInspector();
  const fretNumberScale = useStore((state) => state.arrangeFretNumberScale);
  const diagramMarkers = useStore((state) => state.arrangeDiagramMarkers);
  const chordNameScale = useStore((state) => state.arrangeChordNameScale);
  const nameFactor = scaleFactor(chordNameScale);
  const labelSpaces = LABEL_SPACES * nameFactor;
  const { pressing, begin, end, cancel } = useHoldStrum(chord.fingering, chord.stringSet);
  const lastUpRef = useRef(0);
  const unavailable = !!chord.fingering.unavailable;
  const actualTop = topNoteOf(chord.fingering, chord.stringSet);
  const diagramX = x - diagramWidth / 2;
  // The box names what is VOICED. When the avoid-b9 ladder respelled the
  // GRIP itself (maj7 family pinned by a melody → the 6th chord), the chosen
  // fingering is the truth and the caller's label — stamped before the
  // optimiser ran — would still say maj7 over a 6th-chord diagram.
  const respelled = chord.fingering.chordType !== chord.chordType;
  const boxLabel = respelled ? chordSymbol(chord.displayRoot, chord.fingering.chordType) : label;
  // Everything scales with the staff: the label band used to be a fixed 22px,
  // so at 200% the symbol sat inside its own diagram.
  const spaceSize = diagramWidth / DIAGRAM_WIDTH_SPACES;
  const rootSize = spaceSize * 1.75 * nameFactor;
  // Centre the label on the drawn FRETBOARD, not on the diagram's box: the
  // box's left margin carries the fret-position number, so its centre sits
  // ~5.75% left of the grid and the symbol read as misaligned.
  const labelX = diagramX + diagramWidth * FRETBOARD_CENTER_RATIO;
  const labelBaseline = spaceSize * (LABEL_SPACES - 0.45) * nameFactor;
  const parts = parseChordLabel(boxLabel, chordSymbolStyle);

  const openInspector = () => {
    if (unavailable) return;
    inspect({
      fingering: chord.fingering,
      rootDisplay: chord.displayRoot,
      chordType: chord.fingering.chordType,
      symbol: boxLabel,
      inversion: chord.inversion,
      stringSet: chord.stringSet,
      targetTopNote: chord.targetTopNote,
      leadNote: actualTop,
      title: boxLabel,
      ...(chord.pinKey
        ? { pin: pinContextOf(chord, pinLane(plan, 'guitar'), !!chord.pinned) }
        : {}),
    });
  };

  const handleUp = () => {
    if (unavailable) return;
    const now = performance.now();
    if (now - lastUpRef.current < 280) {
      lastUpRef.current = 0;
      cancel();
    } else {
      lastUpRef.current = now;
      end();
    }
  };

  return (
    <g
      className={`arrangement-grip${pressing ? ' is-pressing' : ''}${chord.pinned ? ' pinned' : ''}`}
      role={unavailable ? undefined : 'button'}
      tabIndex={unavailable ? undefined : 0}
      data-pinned={chord.pinned || undefined}
      aria-label={`${boxLabel} arranged voicing${chord.pinned ? ', pinned' : ''}`}
      onPointerDown={(event: ReactPointerEvent<SVGGElement>) => {
        if (unavailable) return;
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // Pointer capture is optional for SVG in older WebKit builds.
        }
        if (performance.now() - lastUpRef.current >= 280) begin();
      }}
      onPointerUp={handleUp}
      onPointerCancel={cancel}
      // The score steals the capture when a press on the grip turns into a
      // passage drag (PlayableDiagram's rule): end the hold with it, or the
      // grip stays pressed and the next tap plays the stale hold.
      onLostPointerCapture={cancel}
      onDoubleClick={() => {
        cancel();
        openInspector();
      }}
      onKeyDown={(event: ReactKeyboardEvent<SVGGElement>) => {
        if (unavailable) return;
        if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
          event.preventDefault();
          begin();
        }
      }}
      onKeyUp={(event: ReactKeyboardEvent<SVGGElement>) => {
        if (unavailable) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          end();
        }
      }}
    >
      {/* The press target. An SVG <g> only receives pointers where its
          children PAINT — fret lines, dots, text — so without this rect a
          press landing between the strings (most of a fretboard) fell
          through to the score svg and the grip read as broken. The chart's
          cards never had the problem only because they are HTML divs. */}
      <rect
        x={diagramX}
        y={y}
        width={diagramWidth}
        height={spaceSize * labelSpaces + diagramWidth * DIAGRAM_VIEWBOX_ASPECT}
        fill="transparent"
        pointerEvents="all"
      />
      {/* The chord symbol above its own diagram, centred on the drawn grid.
          The engraved lane over the stave is ALSO drawn (user ruling July
          2026) — the same chord twice, deliberately, so what has to hold is
          that the two never touch. Typeset through the SAME parseChordLabel
          the lane uses, so "E♭∆" and "F⁷/E♭" read identically in both. */}
      <title>{boxLabel}</title>
      <text
        x={labelX}
        y={y + labelBaseline}
        className="arrangement-grip-symbol"
        textAnchor="middle"
        fontSize={rootSize}
      >
        {parts.prefix && <tspan>{parts.prefix}</tspan>}
        <tspan data-grip-root>{parts.root}</tspan>
        {parts.sup && (
          <tspan fontSize={rootSize * 0.67} dy={-rootSize * 0.36}>
            {parts.sup}
          </tspan>
        )}
        {parts.bass && (
          <tspan fontSize={rootSize * 0.95} dy={parts.sup ? rootSize * 0.36 : 0}>
            {parts.bass}
          </tspan>
        )}
        {parts.suffix && (
          <tspan fontSize={rootSize} dy={parts.sup && !parts.bass ? rootSize * 0.36 : 0}>
            {parts.suffix}
          </tspan>
        )}
      </text>
      <ChordDiagram
        fingering={chord.fingering}
        rootDisplay={chord.displayRoot}
        chordType={chord.chordType}
        stringSet={chord.stringSet}
        showTitle={false}
        fretNumberScale={fretNumberScale}
        markers={diagramMarkers}
        showIntervals
        highlightAvoid={highlightAvoid}
        className="arrangement-chord-diagram"
        viewport={{
          x: diagramX,
          y: y + spaceSize * labelSpaces,
          width: diagramWidth,
          height: diagramWidth * DIAGRAM_VIEWBOX_ASPECT,
        }}
      />
      {chord.pinned && <PinMark x={diagramX + diagramWidth} y={y} r={spaceSize * 0.35} />}
    </g>
  );
}

/** The sheet's mark for a pinned grip: a small dot in the grip's top-right
 * corner, the ink's own colour (the badge lesson — never a palette token). */
function PinMark({ x, y, r }: { x: number; y: number; r: number }) {
  return (
    <circle
      className="arrangement-pin-mark"
      cx={x - r}
      cy={y + r}
      r={r}
      fill="currentColor"
      opacity={0.75}
      pointerEvents="none"
      data-pin-mark
    />
  );
}

/** The pin picker's inputs for an overlay grip. */
function pinContextOf(
  chord: OverlayVoicing,
  lane: InspectPinContext['lane'],
  pinned: boolean,
  pianoFamily?: string,
): InspectPinContext {
  return {
    lane,
    key: chord.pinKey!,
    pinned,
    chord: {
      root: chord.root,
      displayRoot: chord.displayRoot,
      chordType: chord.chordType,
      targetTopNote: chord.targetTopNote,
      targetTopMidi: chord.targetTopMidi,
      voicingNotes: chord.voicingNotes,
      ...(chord.bass ? { bass: chord.bass } : {}),
    },
    ...(pianoFamily ? { pianoFamily } : {}),
  };
}

/** The overlay's keyboard grip — what the comping lens draws when the Chords
 * menu picks piano diagrams. Same label band as the fretboard version; the
 * diagram itself is the sheet's own PianoKeyboardDiagram nested in the score
 * SVG, click-to-hear through the piano timbre exactly like the sheet strips. */
function ArrangementKeyboard({
  chord,
  label,
  x,
  y,
  diagramWidth,
  chordSymbolStyle,
  plan,
}: {
  chord: OverlayVoicing;
  label: string;
  x: number;
  y: number;
  diagramWidth: number;
  chordSymbolStyle: ChordSymbolStyle;
  plan: PinPlan;
}) {
  const midis = chord.midis!;
  const { inspect } = useInspector();
  const chordNameScale = useStore((state) => state.arrangeChordNameScale);
  const nameFactor = scaleFactor(chordNameScale);
  const labelSpaces = LABEL_SPACES * nameFactor;
  const compInstrument = useStore((state) => state.compInstrument);
  const compPianoFamily = useStore((state) => state.compPianoFamily);
  const openInspector = () =>
    inspect({
      kind: 'piano',
      midis,
      hands: chord.hands,
      rootDisplay: chord.displayRoot,
      chordType: chord.chordType,
      symbol: label,
      ...(chord.pinKey
        ? {
            pin: pinContextOf(
              chord,
              pinLane(plan, 'piano'),
              !!chord.pinned,
              effectivePianoFamily(compInstrument, compPianoFamily),
            ),
          }
        : {}),
    });
  const spaceSize = diagramWidth / KEYBOARD_WIDTH_SPACES;
  // Key height is the slot's; the strip is as wide as its window needs,
  // centred on the chord, and only a window wider than the slot scales down.
  const laneHeight = diagramWidth * KEYBOARD_VIEWBOX_ASPECT;
  const stripWidth = Math.min(diagramWidth, laneHeight * keyboardAspect(midis));
  const rootSize = spaceSize * 1.75 * nameFactor;
  const labelBaseline = spaceSize * (LABEL_SPACES - 0.45) * nameFactor;
  const parts = parseChordLabel(label, chordSymbolStyle);
  return (
    <g
      className={`arrangement-grip arrangement-keyboard${chord.pinned ? ' pinned' : ''}`}
      transform={`translate(${x - diagramWidth / 2} ${y})`}
      data-pinned={chord.pinned || undefined}
      onDoubleClick={openInspector}
    >
      <title>{label}</title>
      {chord.pinned && <PinMark x={diagramWidth} y={0} r={spaceSize * 0.35} />}
      <text
        x={diagramWidth / 2}
        y={labelBaseline}
        className="arrangement-grip-symbol"
        textAnchor="middle"
        fontSize={rootSize}
      >
        {parts.prefix && <tspan>{parts.prefix}</tspan>}
        <tspan data-grip-root>{parts.root}</tspan>
        {parts.sup && (
          <tspan fontSize={rootSize * 0.67} dy={-rootSize * 0.36}>
            {parts.sup}
          </tspan>
        )}
        {parts.bass && (
          <tspan fontSize={rootSize * 0.95} dy={parts.sup ? rootSize * 0.36 : 0}>
            {parts.bass}
          </tspan>
        )}
        {parts.suffix && (
          <tspan fontSize={rootSize} dy={parts.sup && !parts.bass ? rootSize * 0.36 : 0}>
            {parts.suffix}
          </tspan>
        )}
      </text>
      <g transform={`translate(${(diagramWidth - stripWidth) / 2} ${spaceSize * labelSpaces})`}>
        <PianoKeyboardDiagram
          midis={midis}
          width={stripWidth}
          label={`${label} piano voicing`}
          onClick={() => {
            try {
              void getChordPlayer().playMidi(midis, 0.012, 'piano');
            } catch {
              /* no audio */
            }
          }}
        />
      </g>
    </g>
  );
}

function SystemArrangement({
  context,
  chords,
  chordSymbolStyle,
  highlightAvoid,
  piano,
  plan,
}: {
  context: ScoreSystemOverlayContext;
  chords: OverlayVoicing[];
  chordSymbolStyle: ChordSymbolStyle;
  highlightAvoid: boolean;
  /** The whole overlay draws one instrument — pianoOptimized is all-or-null. */
  piano: boolean;
  plan: PinPlan;
}) {
  const systemChords = chords
    .filter((chord) => context.measures.some((measure) => measure.barIndex === chord.barIndex))
    .map((chord) => ({ chord, x: context.xAtBeat(chord.barIndex, chord.beat) }))
    .filter((item): item is { chord: OverlayVoicing; x: number } => item.x !== null)
    .sort((a, b) => a.x - b.x);
  const collisionLayout = layoutDiagramCenters(
    systemChords.map((item) => item.x),
    {
      left: context.left,
      right: context.right,
      preferredWidth: context.spaceSize * (piano ? KEYBOARD_WIDTH_SPACES : DIAGRAM_WIDTH_SPACES),
      gap: context.spaceSize * DIAGRAM_GAP_SPACES,
    },
  );

  return (
    <g className="arrangement-system-overlay" data-arrangement-system={context.systemIndex}>
      {systemChords.map((item, index) =>
        piano ? (
          <ArrangementKeyboard
            key={item.chord.id}
            chord={item.chord}
            label={item.chord.voicedSymbol}
            chordSymbolStyle={chordSymbolStyle}
            x={collisionLayout.centers[index]}
            y={context.top + OVERLAY_TOP_PAD_SPACES * context.spaceSize}
            diagramWidth={collisionLayout.width}
            plan={plan}
          />
        ) : (
          <ArrangementDiagram
            key={item.chord.id}
            chord={item.chord}
            label={item.chord.voicedSymbol}
            highlightAvoid={highlightAvoid}
            chordSymbolStyle={chordSymbolStyle}
            x={collisionLayout.centers[index]}
            y={context.top + OVERLAY_TOP_PAD_SPACES * context.spaceSize}
            diagramWidth={collisionLayout.width}
            plan={plan}
          />
        ),
      )}
    </g>
  );
}

export function ArrangementPane({
  band,
  layout,
  lens = 'melody',
  alternativeShapes = BUILTIN_ALTERNATIVE_SHAPES,
  preferredAlternativeIds = DEFAULT_PREFERRED_ALTERNATIVE_IDS,
  pages = false,
  header,
}: {
  band: BandArrangement;
  layout: ArrangementLayout;
  lens?: ArrangementLens;
  /** Guided studies retain the exact selected drop-voicing recipe. */
  alternativeShapes?: readonly AlternativeShape[];
  preferredAlternativeIds?: readonly string[];
  /** Page view: the lens cells page like every other sheet — the diagram
   * layout by grid ROW (PagedSheet), the score layout by engraved SYSTEM
   * (useScorePages), grips and all. */
  pages?: boolean;
  /** The masthead. A plain sibling when the sheet flows; printed ON page 1's
   * paper in page view (PagedSheet's rule). */
  header?: ReactNode;
}) {
  const score = useStore((state) => state.score);
  const chart = useStore((state) => state.chart);
  const stringSet = useStore((state) => state.stringSet);
  const voicingFamily = useStore((state) => state.voicingFamily);
  const freeStringSet = useStore((state) => state.freeStringSet);
  const avoidB9 = useStore((state) => state.avoidB9);
  const startingInversion = useStore((state) => state.startingInversion);
  const scoreScale = useStore((state) => state.scoreScale);
  const chordNameScale = useStore((state) => state.arrangeChordNameScale);
  const chordSymbolStyle = useStore((state) => state.chordSymbolStyle);
  const { passageIdSet, onSelectPassage } = usePassageSelection();
  const showLyrics = useStore((state) => state.showLyrics);
  const showBarNumbers = useStore((state) => state.showBarNumbers);
  const chordMelodyDensity = useStore((state) => state.chordMelodyDensity);
  const scoreViewPart = useStore((state) => state.scoreViewPart);
  // The FULL projection, secondary staves included: a grand-staff import
  // (realbook's All Blues is a two-stave piano part with the bassline on the
  // F clef) engraves every staff here exactly like the score view — the
  // grips ride the top staff, the rest of the music still shows. The Part
  // choice is honoured on every score surface (it used to pin part 0 here,
  // which made the view-settings Part row vanish whenever a lens was on).
  const view = useMemo(() => {
    const projected = buildScoreView(score, scoreViewPart, showLyrics);
    return {
      ...projected,
      song: { ...projected.song, tempoText: undefined, tempoBpm: undefined },
    };
  }, [score, scoreViewPart, showLyrics]);
  const song = view.song;
  const bars = useMemo(() => song.sections.flatMap((section) => section.bars), [song]);
  // The chord melody is voiced from the band's OWN source, never from the
  // engraved view: `view.song` is the WRITTEN view of whichever part the Part
  // row picked (a tenor reads a major 9th above what sounds, and Part: Bass is
  // a bass line), while the harmony stays concert. Voicing that put every grip
  // over notes nobody plays, recoloured to fit them, and disagreed with what
  // useBandArrangement plays — it voices chartToSong of the concert lead-sheet
  // projection. Its bars map 1:1 onto the form, so the grips still land on the
  // engraved bars.
  const leadSong = useMemo(() => chartToSong(chart), [chart]);
  // BOTH symbols are drawn (user rulings July–Aug 2026), and they no longer
  // duplicate: the engraved lane over the stave keeps the WRITTEN symbol
  // (G7 — the chart's text), while the grip's label names the VOICED reality
  // (G13, G7♯9 — what realizedChordType built around the melody), so a grip
  // that recolours the chord says so right where the dots are. The thing
  // that has to hold is still that the two never touch: the overlay reserves
  // label + diagram exactly, and the engraved lane lives inside `aboveNeeds`
  // below it, so the stack has real clearance rather than the 0.4px it had
  // when this was last drawn both ways.
  // `songWithoutChordSymbols` stays in song.ts, tested, for when the lane is
  // wanted off again.
  const engravedSong = song;
  /**
   * The WRITTEN symbol for a grip's bar and beat — the exact text the score's
   * own lane would have printed, source-true slash basses included. The
   * voicing's `voicedSymbol` comes from the catalogue and drops the bass note
   * ("F7" for a written "F7/Eb"), so it is only the fallback.
   */
  // Your pins for this tune (voicingPins.ts), keyed by written position —
  // the pane builds the melody lens's plan itself, so it applies them itself.
  const pins = useStore((state) => tuneArrangementOf(state)?.pins);
  const pinKeyOf = useMemo(
    () => pinKeyResolver(chart.measures.map((measure) => measure.id)),
    [chart.measures],
  );
  const voiced = useMemo(
    () =>
      lens === 'melody'
        ? buildArrangementVoicings(leadSong, {
            stringSet,
            voicingFamily,
            startingInversion,
            avoidB9,
            freeStringSet,
            alternativeShapes,
            preferredAlternativeIds,
            density: chordMelodyDensity,
            pins: pins?.['melody.guitar'],
            pinKeyOf,
          })
        : [],
    [
      lens,
      leadSong,
      stringSet,
      voicingFamily,
      startingInversion,
      avoidB9,
      freeStringSet,
      alternativeShapes,
      preferredAlternativeIds,
      chordMelodyDensity,
      pins,
      pinKeyOf,
    ],
  );
  // The CHORD-MELODY lens follows the Chords menu too (Aug 2026): the piano
  // chord-melody walk voices the SAME melody-led chords the guitar plan
  // built — written melody note pinned on top, support under it, LH root —
  // aligned 1:1 with `voiced`, so both layouts just attach `midis` to the
  // rows they already draw. Null while the menu says guitar.
  const diagramInstrument = useStore((state) => state.diagramInstrument);
  const pianoMelody = useMemo(
    () =>
      lens === 'melody' && diagramInstrument === 'piano' && voiced.length
        ? voiceLeadPianoMelody(
            voiced.map((chord) => ({
              ...chord,
              pinnedVoicing: pianoPinnedVoicing(pins, 'melody.piano', chord),
            })),
            { avoidB9 },
          )
        : null,
    [lens, diagramInstrument, voiced, avoidB9, pins],
  );
  // Comping lens: the band's voice-led backing line (the same grips the classic
  // Voicings sheet shows) mapped onto the WRITTEN bars — first pass through the
  // form, like optimizedByMeasure, since the notation shows the rolled form.
  // `pianoOptimized ?? optimized` is the SAME seam the diagram sheet reads, so
  // this cell follows the Chords menu for free: the hook computes the piano
  // walk only while the menu draws keyboards, and its items carry the source
  // chord's own fields (1:1 with `optimized`, pinned in the hook's tests)
  // plus `midis`/`held`.
  const compingVoiced = useMemo<OverlayVoicing[]>(() => {
    const source = band.pianoOptimized ?? band.optimized;
    if (lens !== 'comping' || !source) return [];
    const writtenIndex = new Map(chart.measures.map((measure, index) => [measure.id, index]));
    const byBar = new Map<
      number,
      (DiagramVoicing & {
        midis?: number[];
        hands?: { left: number[]; right: number[] };
        pinnedVoicing?: unknown;
      })[]
    >();
    for (const c of source) {
      const list = byBar.get(c.barIndex);
      if (list) list.push(c);
      else byBar.set(c.barIndex, [c]);
    }
    byBar.forEach((list) => list.sort((a, b) => a.beat - b.beat));
    const out: OverlayVoicing[] = [];
    const seen = new Set<string>();
    band.singleFlat.forEach((measure, flatIndex) => {
      if (seen.has(measure.id)) return; // repeated bars share an id — first pass only
      const chords = byBar.get(flatIndex);
      if (!chords || chords.length === 0) return;
      seen.add(measure.id);
      const barIndex = writtenIndex.get(measure.id);
      if (barIndex == null) return;
      for (const c of chords) {
        out.push({
          id: `${barIndex}.${c.id}`,
          barIndex,
          beat: c.beat,
          voicedSymbol: c.symbol,
          displayRoot: c.displayRoot,
          root: c.root,
          chordType: c.chordType,
          stringSet: c.stringSet,
          inversion: c.inversion,
          fingering: c.fingering,
          targetTopNote: c.targetTopNote,
          ...(c.bass ? { bass: c.bass } : {}),
          ...(c.midis ? { midis: c.midis } : {}),
          ...(c.hands ? { hands: c.hands } : {}),
          ...(c.pinKey ? { pinKey: c.pinKey } : {}),
          pinned: c.midis ? !!c.pinnedVoicing : !!c.pin,
        });
      }
    });
    return out;
  }, [lens, band.pianoOptimized, band.optimized, band.singleFlat, chart.measures]);
  const melodyVoiced = useMemo<OverlayVoicing[]>(
    () =>
      pianoMelody
        ? voiced.map((chord, index) => ({
            ...chord,
            midis: pianoMelody[index].midis,
            hands: pianoMelody[index].hands,
            pinned: !!pianoMelody[index].pinnedVoicing,
          }))
        : voiced.map((chord) => ({ ...chord, pinned: !!chord.pin })),
    [voiced, pianoMelody],
  );
  const overlayChords: OverlayVoicing[] = lens === 'comping' ? compingVoiced : melodyVoiced;
  /** Whether this lens draws keyboards — comping follows the hook's gated
   * walk, melody follows the chord-melody walk above; both are the Chords
   * menu's doing. */
  const pianoOverlay = lens === 'comping' ? !!band.pianoOptimized : !!pianoMelody;
  const voicingsByMeasure = useMemo(() => {
    const byMeasure = new Map<string, DiagramRow[]>();
    voiced.forEach((chord, index) => {
      const diagramChord: DiagramRow = {
        ...chord,
        // The card names what is voiced; a ladder-respelled grip (maj7 →
        // 6th under Avoid b9) is known only to its fingering.
        symbol:
          chord.fingering.chordType !== chord.chordType
            ? chordSymbol(chord.displayRoot, chord.fingering.chordType)
            : chord.voicedSymbol,
        targetTopNote: chord.targetTopNote ?? undefined,
        preferredInversion: chord.preferredInversion ?? undefined,
        ...(pianoMelody
          ? {
              midis: pianoMelody[index].midis,
              hands: pianoMelody[index].hands,
              pinnedVoicing: pianoMelody[index].pinnedVoicing,
            }
          : {}),
      };
      const inMeasure = byMeasure.get(chord.barId) ?? [];
      inMeasure.push(diagramChord);
      byMeasure.set(chord.barId, inMeasure);
    });
    return byMeasure;
  }, [voiced, pianoMelody]);
  const { surfaceRef, spaceSize, systemWidth, horizontalMarginSpaces, horizontalEndMarginSpaces } =
    useNotationMetrics(scoreScale, song.key, layout);
  const options = useMemo(
    () => ({
      spaceSize,
      systemWidth,
      // The VIEWED part's clef, as NotationPane engraves it: the main staff
      // draws that part's song, so part 0's clef put Part: Bass on a
      // treble-8vb staff under a stack of ledger lines.
      clef: view.clef,
      // Match the lead-sheet grid. This is a ceiling rather than a forced
      // count: dense measures still wrap earlier when their natural width
      // exceeds the available system width.
      maxBarsPerSystem: 4,
      chordSymbolStyle,
    }),
    [spaceSize, systemWidth, view.clef, chordSymbolStyle],
  );
  const playheadBeat = useBandPlayheadBeat(band, bars);
  // The engraved symbols play here too — same voicings, same gestures as the
  // notation pane and the diagram cards (chart-card parity, all surfaces).
  const playableChords = usePlayableChordSymbols(band);
  const showAnalysis = useStore((state) => state.showAnalysis);
  const analysisLane = useAnalysisLane(song, showAnalysis);
  const renderOverlay = useCallback(
    (context: ScoreSystemOverlayContext) => (
      // With Avoid b9 ON, a grip that still carries the clash was kept
      // because the MELODY forced it (withMelodyPcFallback) — tint it so the
      // override is visible instead of looking like the toggle is broken.
      <SystemArrangement
        context={context}
        chords={overlayChords}
        chordSymbolStyle={chordSymbolStyle}
        highlightAvoid={avoidB9}
        piano={pianoOverlay}
        plan={lens}
      />
    ),
    [overlayChords, chordSymbolStyle, avoidB9, pianoOverlay, lens],
  );
  // Only systems that DRAW a grip reserve the diagram lane — an intro line
  // with no changes keeps the plain stave gap instead of a blank band. The
  // keyboard lane is SHORTER than the fretboard's (wide, low aspect).
  const overlayHeightFor = useCallback(
    (measures: DrawnMeasure[]) =>
      overlayChords.some((chord) => measures.some((measure) => measure.barIndex === chord.barIndex))
        ? pianoOverlay
          ? OVERLAY_SPACES_PIANO + LABEL_SPACES * (scaleFactor(chordNameScale) - 1)
          : OVERLAY_SPACES + LABEL_SPACES * (scaleFactor(chordNameScale) - 1)
        : 0,
    [overlayChords, pianoOverlay, chordNameScale],
  );
  // Asked of the voiced source, like the page's own lens gate: an empty or
  // melody-less viewed part is not a tune without a melody.
  const hasMelody = leadSong.sections.some((section) =>
    section.bars.some((bar) => bar.melody?.some((note) => !note.rest && !note.grace)),
  );
  // The score layout pages by engraved SYSTEM (the diagram layout below
  // pages by grid row through PagedSheet). Called unconditionally — hooks
  // cannot sit behind the early returns underneath.
  const printing = usePrintMode();
  // Paper hides the flowing sheet, so the pager stops measuring it.
  const pager = useScorePages(pages && layout === 'score' && !printing);

  if (lens === 'melody' && !hasMelody) {
    return (
      <p className="empty-hint" data-testid="arrangement-empty">
        Add or import a melody to build a melody-led voicing plan.
      </p>
    );
  }

  if (lens === 'melody' && layout === 'voicings') {
    return (
      <div
        className="arrangement-pane arrangement-voicings-pane"
        data-testid="arrangement-pane"
        data-arrangement-layout="voicings"
        data-arrangement-lens="melody"
        data-score-scale=""
        style={scaleVars({ score: scoreScale, chordName: chordNameScale })}
      >
        <PagedSheet
          chart={chart}
          scale={scoreScale}
          active={pages}
          header={header}
          render={(rowWindow, cells, slots) => (
            <GuitarChartView
              chart={chart}
              scale={scoreScale}
              cellsPerRow={cells}
              slotChords={slots}
              rowWindow={rowWindow}
              byMeasure={voicingsByMeasure}
              pinPlan="melody"
              playingMeasureId={band.playingMeasureId}
              ariaLabel="Melody-led arranged voicings"
              passageMeasureIds={passageIdSet}
              onSelectPassage={onSelectPassage}
              onTapMeasure={band.seekToBar}
              cursorMeasureId={band.startCursor?.measureId}
            />
          )}
        />
      </div>
    );
  }

  // The grips ride ABOVE each system, and ScoreView's system cuts already
  // include that overlay lane — so the spans the pager packs account for the
  // diagrams, and a page break never lands between a system and its grips.
  const arrangementScore = (systemWindow?: readonly [number, number], first = true) => (
    <ScoreView
      barNumbers={showBarNumbers}
      song={engravedSong}
      extraStaves={view.extraStaves}
      staffMeta={view.staffMeta}
      options={options}
      playheadBeat={playheadBeat}
      {...playableChords}
      systemOverlayHeight={overlayHeightFor}
      renderSystemOverlay={renderOverlay}
      {...analysisLane}
      selectedMeasureIds={passageIdSet}
      onSelectPassage={onSelectPassage}
      onTapMeasure={band.seekToBar}
      cursorMeasureId={band.startCursor?.measureId}
      horizontalMarginSpaces={horizontalMarginSpaces}
      horizontalEndMarginSpaces={horizontalEndMarginSpaces}
      systemWindow={systemWindow}
      onSystemSpans={first ? pager.onSystemSpans : undefined}
      ariaLabel={
        lens === 'melody'
          ? `Melody-led arrangement: ${song.title}`
          : `Voice-led comping: ${song.title}`
      }
    />
  );

  return (
    <div
      ref={pager.probeRef}
      className="notation-pane arrangement-pane"
      data-testid="arrangement-pane"
      data-arrangement-layout="score"
      data-arrangement-lens={lens}
      data-score-scale=""
      style={scaleVars({ score: scoreScale, chordName: chordNameScale })}
    >
      {pager.paged ? (
        <div ref={pager.containerRef} className="sheet-pages">
          <SheetPageCards
            gridRef={pager.gridRef}
            columns={pager.columns}
            pageWidth={pager.pageWidth}
            minHeight={pager.minHeight}
            windows={pager.windows}
            render={(win, i) => (
              <>
                {i === 0 && header && <div className="sheet-page-header">{header}</div>}
                <div
                  ref={i === 0 ? surfaceRef : undefined}
                  className="score-surface arrangement-score-surface score-page-surface"
                >
                  {arrangementScore(win, i === 0)}
                </div>
              </>
            )}
          />
        </div>
      ) : (
        <>
          {header}
          <div ref={surfaceRef} className="score-surface arrangement-score-surface">
            {arrangementScore()}
          </div>
        </>
      )}
      {/* One block per system on paper — grips and all, since ScoreView's
          system cuts already include the overlay lane. */}
      {printing && (
        <PrintSheet
          header={header}
          count={pager.systemCount}
          render={(systemWindow) => (
            <div className="score-surface arrangement-score-surface print-score-surface">
              {arrangementScore(systemWindow, false)}
            </div>
          )}
        />
      )}
    </div>
  );
}
